import { ClockIcon } from "@heroicons/react/24/outline";
import {
Dialog,
DialogContent,
DialogTitle
} from "@mui/material";
import BagIcon from "components/BagIcon";
import ConfirmDialog from "components/ConfirmDialog";
import NcInputNumber from "components/NcInputNumber";
import NotifyAddTocart from "components/NotifyAddTocart";
import Prices from "components/Prices";
import { ProductAttributes } from "components/ProductsAttributes";
import SectionPromo2 from "components/SectionPromo2";
import SectionSliderProductCard from "components/SectionSliderProductCard";
import { useCart } from "context/cart/cart.context";
import { CollegeProduct } from "data/data";
import { Product } from "data/types";
import parse from "html-react-parser";
import React,{ FC,useEffect,useMemo,useState } from "react";
import toast from "react-hot-toast";
import { useNavigate,useSearchParams } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import NcImage from "shared/NcImage/NcImage";
import {
useAddToWaitListMutation,
useGetProductQuery,
useGetRelatedProductsQuery,
useGetVariationsQuery
} from "store/api/productsApi";
import { useProducts,useUser } from "store/utils/hooks";
import { generateCartItem } from "utils/generateCartItems";
import getEuro from "utils/getEuro";
import { getVariations } from "utils/getVariations";
import AccordionInfo,{ RichTextFormat } from "./AccordionInfo";
import { StockWarning,Warning } from "./GroupedDetailPage";
import ModalViewAllReviews from "./ModalViewAllReviews";

export interface ProductDetailPageProps {
  className?: string;
}

type AvailableCoupon = {
  id?: number;
  code: string;
  name?: string;
  value?: string | number;
  type?: string;
  scope?: "products" | "shipping";
};

const parseAvailableCoupons = (source: any): AvailableCoupon[] => {
  if (!source) return [];

  const rawCoupons = [
    ...(Array.isArray(source?.coupons) ? source.coupons : []),
    ...(Array.isArray(source?.available_coupons) ? source.available_coupons : []),
    ...(Array.isArray(source?.coupones) ? source.coupones : []),
    ...(source?.coupon ? [source.coupon] : []),
    ...(source?.available_coupon ? [source.available_coupon] : []),
  ];

  const parsed = rawCoupons
    .map((coupon: any) => {
      if (typeof coupon === "string") {
        const code = coupon.trim();
        return code ? { code, name: code } : null;
      }

      if (!coupon || typeof coupon !== "object") return null;

      const code = String(
        coupon?.code ||
        coupon?.coupon_code ||
        coupon?.name ||
        ""
      ).trim();

      if (!code) return null;

      return {
        id: coupon?.id,
        code,
        name: coupon?.name || code,
        value: coupon?.value,
        type: coupon?.type,
        scope: coupon?.scope,
      };
    })
    .filter(Boolean) as AvailableCoupon[];

  return parsed.filter(
    (coupon, index, arr) =>
      (coupon.scope || "products") === "products" &&
      arr.findIndex((it) => it.code.toLowerCase() === coupon.code.toLowerCase()) === index
  );
};

const normalizeText = (value: unknown) =>
  String(value || "")
    .trim()
    .toLowerCase();

const ProductDetailPage: FC<ProductDetailPageProps> = ({ className = "" }) => {
  const [params] = useSearchParams();
  // --- FIX 1: Obtenemos el ID actual como número para comparaciones estrictas ---
  const currentId = Number(params.get("id"));

  const products = useProducts() as CollegeProduct[];
  const { data: apiProduct } = useGetProductQuery(currentId);
  const { data: relatedProducts } = useGetRelatedProductsQuery(currentId);
  const [addToWaitList, { isLoading: isLoadingAddToWaitList }] = useAddToWaitListMutation();
  const [isConfirmWaitingList, setIsConfirmWaitingList] = useState(false);
  const [selectedProductCoupon, setSelectedProductCoupon] = useState<AvailableCoupon | null>(null);
  const user = useUser();
  const navigate = useNavigate();
  const { items, addItemToCart } = useCart();
  
  const data = (products || apiProduct) && {
    ...(products?.find((it) => it.id === currentId) || {}),
    ...(apiProduct || {}),
  };

  const handleAddToWaitlist = async (product: CollegeProduct) => {
    try {
      const res = await addToWaitList(product.id);
      if (res.error) {
        toast.error(res.error?.data?.message || "Error al agregar a la lista de espera");
      } else {
        toast.success("Se ha suscrito a la lista de espera del producto, se te notificará cuando esté disponible", {
          duration: 5000,
        });
      }
    } catch (error) {
      console.error("Error agregando a la lista de espera:", error);
      toast.error("Error al agregar a la lista de espera");
    }
  };

  const [attributes, setAttributes] = useState<{ [key: string]: string }>({});
  const variations = getVariations(data?.attributes);
  const [qualitySelected, setQualitySelected] = React.useState(1);
  const [isShowGuide, setIsShowGuide] = React.useState(false);
  const [isOpenModalViewAllReviews, setIsOpenModalViewAllReviews] = useState(false);
  
  // --- FIX 2: selectedImage inicia en null y SOLO se usa para clicks manuales ---
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  
  const { data: productVariations, isLoading: isLoadingVariations } = useGetVariationsQuery(params.get("id"));
  
  // --- FIX: Encontrar la variación correcta basada en los atributos seleccionados por el usuario ---
  const product = useMemo(() => {
    if (!productVariations || productVariations.length === 0) {
      return data;
    }
    
    // Si no hay atributos seleccionados, devolver el producto por defecto
    if (Object.keys(attributes).length === 0) {
      return data;
    }
    
    const selectedEntries = Object.entries(attributes).map(([name, value]) => [
      normalizeText(name),
      normalizeText(value),
    ]);

    const matchedVariation = productVariations.find((variation: Product) => {
      const attrMap = new Map<string, string[]>();

      if (variation.attributes && Array.isArray(variation.attributes)) {
        variation.attributes.forEach((attr: any) => {
          const attrName = normalizeText(attr?.name);
          const termValues = Array.isArray(attr?.terms)
            ? attr.terms
                .map((term: any) => normalizeText(term?.name))
                .filter(Boolean)
            : [];
          const optionValues = [normalizeText(attr?.option), ...((attr?.options || []).map((opt: any) => normalizeText(opt)))]
            .filter(Boolean);
          attrMap.set(attrName, [...new Set([...termValues, ...optionValues])]);
        });
      }

      const allSelectedMatchByAttr = selectedEntries.every(([selectedName, selectedValue]) => {
        const variationValues = attrMap.get(selectedName) || [];
        return variationValues.includes(selectedValue);
      });

      return allSelectedMatchByAttr;
    });
    
    return matchedVariation;
  }, [params, productVariations, attributes, data]);

  // --- FIX 3: ELIMINAMOS el useEffect que hacía setSelectedImage(product.image).
  // Eso causaba el bug al persistir la imagen vieja. Ahora la imagen por defecto se calcula al vuelo.

  // --- FIX 4: Reset total al cambiar de ID ---
  useEffect(() => {
    setQualitySelected(1);
    setAttributes({});
    setSelectedImage(null); // Borramos cualquier selección manual anterior inmediatamente
    setSelectedProductCoupon(null);
  }, [params]);

  // --- FIX: La comparación es true si:
  // 1. product.id === currentId (producto principal) O
  // 2. product.parent_id === currentId (variación del producto actual)
  const isDataFresh = (product?.id === currentId) || (product?.parent_id === currentId);
  const imageToShow = selectedImage 
      ? selectedImage 
      : (isDataFresh ? product?.image : "");  


  const itemCart = items.find(({ id }) => {
    return product?.id === id;
  });

  const isInCart =
    data &&
    items.some(({ pivot_parent_id, parent_id, id }) => {
      if (data.type === "grouped") {
        return data.id === pivot_parent_id;
      }
      if (data.type === "variable") {
        return data.id === parent_id;
      }
      return data.id === id;
    });

  const isSelected = Object.keys(variations).length
    ? Object.keys(attributes) &&
      Object.keys(variations).every((variation) =>
        attributes.hasOwnProperty(variation)
      )
    : true;

  const availableCoupons = useMemo(
    () => parseAvailableCoupons(product || data),
    [product, data]
  );

  function handleAttribute(attribute: { [key: string]: string }) {
    setSelectedImage(null); // Resetear selección manual cuando se cambia un atributo
    setSelectedProductCoupon(null); // Al cambiar de variación se limpia el cupón seleccionado
    setAttributes((prev) => ({
      ...prev,
      ...attribute,
    }));
  }

  useEffect(() => {
    if (!selectedProductCoupon) return;

    const couponStillAvailable = availableCoupons.some(
      (coupon) =>
        coupon.code.toLowerCase() === selectedProductCoupon.code.toLowerCase()
    );

    if (!couponStillAvailable) {
      setSelectedProductCoupon(null);
    }
  }, [availableCoupons, selectedProductCoupon]);

  const addToCart = () => {
    const item = generateCartItem(
      {
        ...data,
        ...product,
        name: data ? data.name : "",
        limit_store: data.limit_store,
      },
      attributes,
      selectedProductCoupon
    );
    addItemToCart(item, qualitySelected);
    notifyAddTocart();
  };

  const notifyAddTocart = () => {
    const item = generateCartItem(
      { ...data, ...product, name: data ? data.name : "" },
      attributes,
      selectedProductCoupon
    );
    toast.custom(
      (t) => (
        <NotifyAddTocart
          productImage={imageToShow || ""} // Usamos la imagen calculada
          qualitySelected={qualitySelected}
          show={t.visible}
          name={item.name}
          attributes={item.attributes}
          price={product?.price ? product?.price : 0}
        />
      ),
      { position: "top-right", id: "nc-product-notify", duration: 3000 }
    );
  };

  const hideSizeGuide = () => {
    setIsShowGuide(false);
  };

  const showSizeGuide = () => {
    setIsShowGuide(true);
  };

  // Solo calculamos imágenes si los datos son frescos
  const allProductImages = (isDataFresh && product?.images?.length)
    ? [product.image, ...product.images]
    : [];

  const uniqueProductImages = [...new Set(allProductImages)];

  const handleImageClick = (image: string) => {
    setSelectedImage(image);
  };

  const handleTakeCoupon = (coupon: AvailableCoupon) => {
    if (!coupon.id) {
      toast.error("Este cupón no tiene identificador válido para aplicar por producto.");
      return;
    }
    setSelectedProductCoupon(coupon);
    toast.success(`Cupón ${coupon.code} vinculado al producto en tu carrito.`);
  };

  const selectedCouponDiscount = useMemo(() => {
    if (!selectedProductCoupon || !product?.price) return 0;
    const couponValue = Number(selectedProductCoupon.value || 0);
    if (!Number.isFinite(couponValue) || couponValue <= 0) return 0;

    const couponType = String(selectedProductCoupon.type || "").toLowerCase();
    if (couponType === "percent" || couponType === "percentage") {
      return Number(product.price) * (couponValue / 100);
    }
    return couponValue;
  }, [selectedProductCoupon, product?.price]);

  const discountedProductPrice = useMemo(() => {
    if (!product?.price) return null;
    return Math.max(Number(product.price) - selectedCouponDiscount, 0);
  }, [selectedCouponDiscount, product?.price]);

  const currentPromotionLabel = useMemo(() => {
    const variationLabel = (product as Product | undefined)?.promotion_label;
    if (variationLabel != null) return variationLabel;
    return data?.promotion_label;
  }, [product, data]);

  const renderSectionContent = () => {
    return (
      <div className="space-y-7 2xl:space-y-8">
        <div>
          {/* Usamos data?.name directamente pero podrías poner un Skeleton si isFetching es true */}
          <h2 className="text-2xl sm:text-3xl font-semibold">{data?.name}</h2>
          {data?.short_description && (
            <RichTextFormat className="mt-4">
              {parse(data?.short_description)}
            </RichTextFormat>
          )}
          <div>
            {variations && Object.keys(variations).length !== 0 ? (
              <div className={`mt-4`}>
                {Object.keys(variations).map((variation) => {
                  return (
                    <ProductAttributes
                      key={variation}
                      title={variation}
                      attributes={variations[variation]}
                      active={attributes[variation]}
                      onClick={handleAttribute}
                    />
                  );
                })}
              </div>
            ) : null} 
            {/* Quitamos el "Cargando..." estático, mejor manejar loading global o skeleton */}
          </div>

          {data?.size_guide && (
            <div className="pb-3 border-b border-gray-300">
              <p
                onClick={showSizeGuide}
                className="font-semibold transition-all underline hover:opacity-50 cursor-zoom-in hover:no-underline"
              >
                Guía de tallas
              </p>
            </div>
          )}

          <div className="flex items-center mt-5 space-x-4 sm:space-x-5">
            <Prices
              contentClass="py-1 px-2 md:py-1.5 md:px-3 text-lg font-semibold"
              price={product?.price}
              price_html={product?.price_html}
              refund_price={product?.sale_price}
            />
            <div className="flex items-center">
            </div>
          </div>
          {selectedProductCoupon && discountedProductPrice !== null && selectedCouponDiscount > 0 && (
            <div className="mt-2 text-sm">
              <p className="text-slate-500">
                Precio normal: <span className="line-through">{getEuro(Number(product?.price || 0))}</span>
              </p>
              <p className="font-semibold text-green-700">
                Con cupón de prenda: {getEuro(discountedProductPrice)}
              </p>
            </div>
          )}

          {availableCoupons.length > 0 && (
            <div className="mt-5 rounded-2xl border border-green-200 bg-green-50 p-4">
              <p className="text-sm font-semibold text-green-800">
                Cupón disponible para esta prenda
              </p>
              <div className="mt-3 space-y-2">
                {availableCoupons.map((coupon) => {
                  const isSelectedCoupon = selectedProductCoupon?.code?.toLowerCase() === coupon.code.toLowerCase();
                  const discountLabel = coupon.value
                    ? coupon.type === "percent"
                      ? `${coupon.value}%`
                      : `${coupon.value}€`
                    : "";

                  return (
                    <div
                      key={coupon.code}
                      className="flex items-center justify-between rounded-xl border border-green-200 bg-white px-3 py-2"
                    >
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{coupon.name || coupon.code}</p>
                        <p className="text-xs text-slate-500">
                          Código: <span className="font-mono">{coupon.code}</span>
                          {discountLabel ? ` · Descuento: ${discountLabel}` : ""}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleTakeCoupon(coupon)}
                        disabled={!coupon.id}
                        className="rounded-xl border border-green-300 bg-green-100 px-3 py-1.5 text-xs font-semibold text-green-800 hover:bg-green-200 transition-colors"
                      >
                        {!coupon.id
                          ? "Cupón no aplicable"
                          : isSelectedCoupon
                          ? "Cupón tomado"
                          : "Tomar cupón"}
                      </button>
                    </div>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-slate-600">
                Se guardará en este producto al agregarlo al carrito.
              </p>
            </div>
          )}
        </div>
    
        <div className="flex space-x-3.5">
          <div className="flex mb-auto items-center justify-center bg-slate-100/70 dark:bg-slate-800/70 px-2 py-3 sm:p-3.5 rounded-full">
            <NcInputNumber
              defaultValue={qualitySelected}
              onChange={setQualitySelected}
              max={
                data?.limit_store
                  ? 1
                  : product && !product.store_without_stock
                    ? product.stock_quantity - (itemCart?.quantity || 0)
                    : undefined
              }
            />
          </div>
          <ButtonPrimary
            className="flex-1 flex-shrink-0"
            disabled={
              isLoadingVariations ||
              !product ||
              !isSelected ||
              (isInCart && data?.limit_store) ||
              ((itemCart?.quantity || 0) + qualitySelected >
                product?.stock_quantity &&
                !Boolean(product.store_without_stock))
            }
            onClick={user ? addToCart : () => navigate("/login")}
          >
            <BagIcon className="hidden sm:inline-block w-5 h-5 mb-0.5" />
            <span className="ml-3">Agregar al carrito</span>
          </ButtonPrimary>
        </div>
        <div className="space-y-2">
          {product &&
            product?.stock_quantity <
            (itemCart?.quantity || 0) + qualitySelected &&
            isSelected && (
              <>
                {product.store_without_stock ? (
                  <StockWarning
                    noStock={(product as Product).stock_quantity === 0}
                  />
                ) : (
                  <Warning>
                    No hay stock suficiente para realizar esta compra.
                  </Warning>
                )}
                {(product as Product).stock_quantity === 0 && (product as Product).is_available_in_wait_list && 
                  <>
                    <div className="h-2"></div>
                    <ButtonSecondary
                      className="border-2 w-full border-slate-500"
                      loading={isLoadingAddToWaitList}
                      onClick={()=>setIsConfirmWaitingList(true)}
                    >
                      <ClockIcon className="hidden sm:inline-block w-5 h-5 mb-0.5" />
                      <span className="ml-3">Agregar a la lista de espera</span>
                    </ButtonSecondary>
                  </>
                }
              </>
            )}
          {Boolean(data?.limit_store) && (
            <Warning>
              Este producto está limitado a una compra por estudiante.
            </Warning>
          )}
        </div>
        
        <hr className=" 2xl:!my-10 border-slate-200 dark:border-slate-700"></hr>
        
        {data && (
          <AccordionInfo
            data={[
              {
                name: "Envíos y Devoluciones",
                content: data?.description,
              },
              {
                name: "Normas de lavado y secado",
                content: data?.instructions,
              },
            ]}
          />
        )}
      </div>
    );
  };

  return (
    <div className={`nc-ProductDetailPage ${className}`}>
      {/* MAIn */}
      <main className="container mt-5 lg:mt-11">
        <div className="lg:flex">
          {/* CONTENT */}
          <div className="w-full lg:w-[55%] ">
            {/* HEADING */}
            <div className="relative">
              {currentPromotionLabel != null && (
                <div className="ribbon">
                  {currentPromotionLabel}
                </div>
              )}
              <div className="aspect-square">
                {/* --- FIX 6: Renderizado condicional estricto --- */}
                {/* Si imageToShow está vacío (porque estamos cargando datos frescos), muestra placeholder o div vacío */}
                {/* El key={currentId} fuerza a React a recrear el nodo imagen si cambia el ID, evitando "fantasmas" */}
                <img
                  key={currentId} 
                  src={imageToShow}
                  className={`w-full rounded-2xl object-contain ${!imageToShow ? 'opacity-0' : 'opacity-100 transition-opacity'}`}
                  alt={`${product?.name || 'Product'} main image`}
                />
                {!imageToShow && (
                    /* Optional: Skeleton Loader simple mientras carga */
                    <div className="absolute inset-0 bg-gray-100 animate-pulse rounded-2xl" />
                )}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3 mt-3 sm:gap-4 sm:mt-4">
              {uniqueProductImages.length > 0 ? (
                uniqueProductImages.map((image, index) => (
                  <div
                    key={`${currentId}-${index}`} // Key única por producto
                    className={`aspect-square cursor-pointer transition-all duration-200 ${
                      imageToShow === image 
                        ? 'ring-2 ring-primary-500 rounded-lg' 
                        : 'opacity-80 hover:opacity-100'
                    }`}
                    onClick={() => handleImageClick(image)}
                  >
                    <img
                      src={image}
                      className="w-full rounded-lg object-cover"
                      alt={`${product?.name || 'Product'} image ${index + 1}`}
                    />
                  </div>
                ))
              ) : null}
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="w-full lg:w-[45%] pt-10 lg:pt-0 lg:pl-7 xl:pl-9 2xl:pl-10">
            {renderSectionContent()}
          </div>
        </div>

        {/* DETAIL AND REVIEW */}
        <div className="mt-12 sm:mt-16 space-y-10 sm:space-y-16">
          <hr className="border-slate-200 dark:border-slate-700" />

          {/* RELATED PRODUCTS SECTION */}
          <div className="relative">
            {relatedProducts && relatedProducts.length > 0 ? (
              <SectionSliderProductCard
                heading="Productos relacionados"
                subHeading=""
                headingFontClassName="text-2xl font-semibold"
                headingClassName="mb-10 text-neutral-900 dark:text-neutral-50"
                data={relatedProducts}
              />
            ) : (
              <>
                <h2 className="text-2xl font-semibold mb-10 text-neutral-900 dark:text-neutral-50">
                  Productos relacionados
                </h2>
                <p>No hay productos relacionados a este producto.</p>
              </>
            )}
          </div>

          {/* SECTION */}
          <div className="pb-20 xl:pb-28 lg:pt-14">
            <SectionPromo2 />
          </div>
        </div>
      </main>

      {/* MODAL VIEW ALL REVIEW */}
      <ModalViewAllReviews
        show={isOpenModalViewAllReviews}
        onCloseModalViewAllReviews={() => setIsOpenModalViewAllReviews(false)}
      />
      <Dialog open={isShowGuide} onClose={hideSizeGuide}>
        <div className="flex justify-between items-center">
          <DialogTitle>
            <b>Guía de Tallas:</b> {data?.name}
          </DialogTitle>
          <ButtonClose onClick={hideSizeGuide} className="mr-4"></ButtonClose>
        </div>
        <DialogContent>
          <NcImage src={data?.size_guide} />
        </DialogContent>
      </Dialog>
      <ConfirmDialog
      open={isConfirmWaitingList}
      hide={()=>setIsConfirmWaitingList(false)}
      isLoading={isLoadingAddToWaitList}
      title={product?.composed_name || product?.name}
      desc="¿Estás seguro de que deseas añadir este producto a la lista de espera?"
      onConfirm={async () => {
        await handleAddToWaitlist(product)
      }}
      />
    </div>
  );
};

export default ProductDetailPage;
